import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Stack,
  TextField,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { listSchoolsDirect } from '@timetable/api';
import type { UserFilters as UserFiltersType } from '@timetable/types';

interface UserFiltersProps {
  filters: UserFiltersType & { search?: string };
  onChange: (filters: UserFiltersType & { search?: string }) => void;
}

/**
 * UserFilters Component
 * 
 * Provides filtering controls for the user management page:
 * - School filter dropdown
 * - Role filter dropdown
 * - Status filter dropdown
 * - Search by name/email
 * 
 * Requirements: 2.4.1, 3.4.1, 3.4.4
 */
export function UserFilters({ filters, onChange }: UserFiltersProps) {
  const [searchValue, setSearchValue] = useState(filters.search || '');

  // Fetch schools for filter dropdown
  const { data: schools } = useQuery({
    queryKey: ['schools'],
    queryFn: listSchoolsDirect,
  });

  const handleFilterChange = (field: keyof UserFiltersType | 'search', value: string) => {
    onChange({
      ...filters,
      [field]: value || undefined,
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    // Debounce search - update filters after user stops typing
    const timeoutId = setTimeout(() => {
      handleFilterChange('search', value);
    }, 300);
    return () => clearTimeout(timeoutId);
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        {/* Search by name/email */}
        <TextField
          placeholder="Search by name or email..."
          value={searchValue}
          onChange={(e) => handleSearchChange(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* School filter */}
        <TextField
          select
          label="School"
          value={filters.schoolId || ''}
          onChange={(e) => handleFilterChange('schoolId', e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All Schools</MenuItem>
          {schools?.map((school) => (
            <MenuItem key={school.school_id} value={school.school_id}>
              {school.school_name}
            </MenuItem>
          ))}
        </TextField>

        {/* Role filter */}
        <TextField
          select
          label="Role"
          value={filters.role || ''}
          onChange={(e) => handleFilterChange('role', e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All Roles</MenuItem>
          <MenuItem value="super_admin">Super Admin</MenuItem>
          <MenuItem value="admin">Admin</MenuItem>
          <MenuItem value="standard">Standard</MenuItem>
          <MenuItem value="teacher">Teacher</MenuItem>
        </TextField>

        {/* Status filter */}
        <TextField
          select
          label="Status"
          value={filters.status || ''}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
      </Stack>
    </Paper>
  );
}
